/*
 * r16n (2026-10-21), design D:/NecroWork/r15k10-cf-evidence/DESIGN.md 4 / stats.md 2c: story shares by audience on the Slot Stats
 * page - "Your story" (everyone) and "Close Friends" (the account's whole Close Friends list, setting 1109/1110).
 *
 * WHAT THE SERVER SENDS (r16n uiapiStats, from igacc_stats; switch necro.server.storyShareStats - OFF or a server before r16n omits
 * the fields):
 *   storySharesEveryoneDoneThisDaySticky / storySharesEveryoneDoneInPreviousDays          (int / CSV, newest first, 90 days)
 *   storySharesCloseFriendsDoneThisDaySticky / storySharesCloseFriendsDoneInPreviousDays
 * NULL = not recorded (a slot that never shared, or a worker before 15k.10). The worker (15k.10) counts a share by the audience it
 * PROVED on the recipients sheet before Share, when Instagram showed Done. A share skipped because Close Friends could not be
 * selected is in neither series; the two never overlap, so their sum is every counted story share. The day anchor is the action
 * counters' (lastResetDateTimeDays), as for every action type.
 *
 * WHY ITS OWN CARD, NOT TWO MORE TYPES IN THE "ACTIONS DONE" STACK (the design's first idea, stats.md 2c.1): the dataviz validator
 * finds no 9th / 10th vivid colour that stays >= 15 (normal vision) from all eight action colours on #0e121f - the only survivors
 * are olive / dull browns and one electric cyan (search 2026-10-21, stats.css records it) - and at 1-2 shares a day against
 * hundreds of likes the two segments would be sub-pixel slivers on top of the stack, so the split the owner asked about ("50% to
 * all users, 50% to close friends") could not be read there. Here the two audiences have their own scale, a two-colour stack, the
 * Close Friends share as a figure, a legend, a tooltip and a table twin. The "Actions done" card, its tabs, stack and totals are
 * unchanged (the owner's "Stacked, all 8 types").
 */
import { dailySeries, parseHistoryCsv, isoDay, dayLabel, shiftDay, daysBetween } from './slotHistory';
import { actionBars, WEEKLY_FROM_RANGE } from './slotStatsView';

export const STORY_SHARES_TAG = '[STORY-CF]';

/** The two audiences, bottom of the stack first. The colours are stats.css tokens (validated there). */
export const SHARE_SERIES = [
  {
    key: 'storySharesEveryone', label: 'Your story (everyone)', short: 'Your story',
    today: 'storySharesEveryoneDoneThisDaySticky', csv: 'storySharesEveryoneDoneInPreviousDays', color: 'var(--viz-share-ys)',
  },
  {
    key: 'storySharesCloseFriends', label: 'Close Friends', short: 'Close Friends',
    today: 'storySharesCloseFriendsDoneThisDaySticky', csv: 'storySharesCloseFriendsDoneInPreviousDays', color: 'var(--viz-cf)',
  },
];
export const [YS_KEY, CF_SHARE_KEY] = SHARE_SERIES.map((s) => s.key);

export const shareColor = (key) => {
  const s = SHARE_SERIES.find((x) => x.key === key);
  return s ? s.color : 'var(--viz-1)';
};
export const shareLabel = (key) => {
  const s = SHARE_SERIES.find((x) => x.key === key);
  return s ? s.label : key;
};

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k) && o[k] !== undefined;
const num = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'null') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const sum = (vals) => vals.reduce((a, v) => a + v, 0);

/**
 * Everything the Story shares card draws, from the /stats answer, over the page's range (7 / 30 / 90 days).
 * @returns {{
 *   sent: boolean,          the server knows the fields (false: a server before r16n, or its stats switch OFF)
 *   present: boolean,       at least one audience has a recorded day
 *   series: Array<{key,label,short,color,sent,present,start}>, start: the first recorded day (YYYY-MM-DD) or null
 *   keys: string[],         the present audiences, bottom of the stack first
 *   day0, day0Known, today, days (oldest first), weekly,
 *   rows: Array<{date, partial, total, <key>: n|null}>  (oldest first; total null when no audience was recorded that day)
 *   bars: actionBars() over rows ('all' = the day's total, parts = each audience)
 *   totals: { all, everyone, closeFriends, cfShare (0..1 | null), recordedDays, avg (per complete recorded day) | null },
 *   partial: boolean,       the range holds today so far
 *   allZero: boolean,       every recorded day is 0
 *   lateStarts: Array<{key,label,date}>  an audience recorded from a day after the range's first recorded day
 * }}
 */
export function storySharesHistory(statsRaw, range, now = new Date()) {
  const src = (statsRaw && (statsRaw.stats || statsRaw.igAccStats)) || statsRaw || {};
  const today = dayLabel(0, now);
  const resetDay = isoDay(src.lastResetDateTimeDays);
  const day0 = resetDay || today;
  const weekly = range >= WEEKLY_FROM_RANGE;
  const series = SHARE_SERIES.map((m) => {
    const sent = has(src, m.today) || has(src, m.csv);
    const todayKnown = sent && num(src[m.today]) !== null;
    const historyKnown = sent && parseHistoryCsv(src[m.csv]).length > 0;
    const present = todayKnown || historyKnown;
    const data = present ? dailySeries(src[m.today], src[m.csv]) : [];
    return {
      key: m.key, label: m.label, short: m.short, color: m.color, sent, present, data,
      start: present ? shiftDay(day0, data.length - 1) : null,
    };
  });
  const sent = series.some((s) => s.sent);
  const live = series.filter((s) => s.present);
  const base = {
    sent, present: live.length > 0, series, keys: live.map((s) => s.key), day0, day0Known: resetDay !== null, today, weekly,
    days: [], rows: [], bars: [],
    totals: { all: null, everyone: null, closeFriends: null, cfShare: null, recordedDays: 0, avg: null },
    partial: false, allZero: false, lateStarts: [],
  };
  if (!live.length) return base;
  const longest = Math.max(...live.map((s) => s.data.length));
  const count = Math.min(range, longest);
  const days = [];
  for (let k = count - 1; k >= 0; k--) days.push(shiftDay(day0, k));
  const partialDay = day0 === today ? day0 : null;
  const rows = days.map((date) => {
    const i = daysBetween(day0, date);
    const row = { date, partial: date === partialDay, total: null };
    live.forEach((s) => {
      const v = i < s.data.length ? s.data[i] : null;
      row[s.key] = v;
      if (v !== null) row.total = (row.total || 0) + v;
    });
    return row;
  });
  const keys = live.map((s) => s.key);
  const bars = actionBars(rows, 'all', weekly, keys);
  const of = (key) => {
    if (!keys.includes(key)) return null;
    const vals = rows.map((r) => r[key]).filter((v) => v !== null);
    return vals.length ? sum(vals) : null;
  };
  const recorded = rows.filter((r) => r.total !== null);
  const full = recorded.filter((r) => !r.partial);
  const all = recorded.length ? sum(recorded.map((r) => r.total)) : null;
  const everyone = of(YS_KEY);
  const closeFriends = of(CF_SHARE_KEY);
  // the Close Friends share needs BOTH audiences counted over the range: with one not recorded, the split is not known
  const both = everyone !== null && closeFriends !== null;
  const firstRecorded = recorded.length ? recorded[0].date : null;
  return {
    ...base,
    days,
    rows,
    bars,
    totals: {
      all,
      everyone,
      closeFriends,
      cfShare: both && all > 0 ? closeFriends / all : null,
      recordedDays: recorded.length,
      avg: full.length ? sum(full.map((r) => r.total)) / full.length : null,
    },
    partial: recorded.some((r) => r.partial),
    allZero: recorded.length > 0 && recorded.every((r) => r.total === 0),
    lateStarts: firstRecorded
      ? live.filter((s) => daysBetween(s.start, firstRecorded) > 0 && daysBetween(days[days.length - 1], s.start) >= 0)
        .map((s) => ({ key: s.key, label: s.label, date: s.start }))
      : [],
  };
}

/** "48%" / "0%" / "100%" / "<1%" - the Close Friends share of a period's counted story shares. */
export function fmtShare(p) {
  if (p === null || p === undefined || !Number.isFinite(p)) return null;
  if (p > 0 && p < 0.005) return '<1%';
  if (p < 1 && p > 0.995) return '>99%';
  return `${Math.round(p * 100)}%`;
}

/** The tooltip's extra line for one bar: the Close Friends share of that day's (week's) counted shares. */
export function barShareLine(bar) {
  if (!bar || !bar.parts) return null;
  const cf = bar.parts[CF_SHARE_KEY];
  const ys = bar.parts[YS_KEY];
  if (cf === null || cf === undefined || ys === null || ys === undefined) return null;
  const all = cf + ys;
  if (!(all > 0)) return null;
  return `${fmtShare(cf / all)} of these shares went to Close Friends.`;
}
